//import React from 'react';
//import './home.css';
import React, { useEffect, useState } from 'react';
import './home.css'; // Make sure this CSS file is imported in your project

import { useNavigate } from 'react-router-dom';
import LoadingIndicator from '@diditusa/shared/web_components/LoadingIndicator/loadingIndicator';
import { useAuth } from '../../context/AuthContext';
import {
  useLazyGetCompanyJobsSummaryQuery,
  useLazyGetCompanyQuery,
  useLazyGetCompanySummaryQuery,
} from '@diditusa/shared/api/companyApi';
import { getErrorMessage } from '@diditusa/shared/api/apiUtils';
import { Box, Button, Typography } from '@mui/material';
import { JobStatus } from '@diditusa/shared/types/jobTypes';
import BasicTable from '@diditusa/shared/web_components/BasicTable/BasicTable';
import { format } from 'date-fns';
import MetricsCard from '@diditusa/shared/web_components/MetricsCard/MetricsCard';
import { BasicTableHeader } from '@diditusa/shared/types/commonTypes';

const ManagerHome: React.FC = () => {
  const { isAuthenticated, user: authUser } = useAuth();
  const companyId = authUser?.company?.id;
  const navigate = useNavigate();
  const [getCompanySummary, { data: companySummary, ...getCompanySummaryState }] =
    useLazyGetCompanySummaryQuery();
  const [getJobsSummary, { data: jobsSummary, ...getJobsSummaryState }] =
    useLazyGetCompanyJobsSummaryQuery();

  useEffect(() => {
    if (companyId) {
      getCompanySummary(companyId);
      getJobsSummary(companyId);
    }
  }, [companyId]);

  const renderCompanyStats = () => {
    const metrics: { label: string; value: string; onClick: () => void }[] = [
      {
        label: 'Total Jobs',
        value: `${companySummary ? companySummary.totalJobs : '-'}`,
        onClick: () => navigate('/company/jobs'),
      },
      {
        label: 'Total Agents',
        value: `${companySummary ? companySummary.totalAgents : '-'}`,
        onClick: () => navigate('/company/agents'),
      },
      {
        label: 'Customer Satisfaction',
        value: `${companySummary ? `${companySummary.customerStatisfaction || 0} / 5` : '-'}`,
        onClick: () => {},
      },
    ];
    return metrics.map((metric) => (
      <Box
        display='flex'
        flexGrow='1'
        maxWidth='30%'
        sx={{ ':hover': { cursor: 'pointer' } }}
        onClick={metric.onClick}
      >
        <MetricsCard metric={metric} />
      </Box>
    ));
  };

  const renderUnassignedJobsSummary = () => {
    if (getJobsSummaryState.isLoading) return <LoadingIndicator isVisible />;
    if (getJobsSummaryState.isError)
      return <p>Error loading jobs: {getErrorMessage(getJobsSummaryState.error)}</p>;

    interface JobDetails {
      id: number;
      reservationCode: string;
      user: string;
      location: string;
      startDate: Date;
      startTime: Date;
      duration: number;
      package: string;
      status: JobStatus;
    }

    type JobDetailsKey = keyof JobDetails;
    const jobDetails: JobDetails[] =
      jobsSummary?.unassignedJobs?.map((j) => ({
        id: j.id,
        reservationCode: j.reservationCode,
        location: j.location,
        user: `${j.createdBy.firstName} ${j.createdBy.lastName}`,
        agents:
          j.jobAssignments?.map((ja) => `${ja.user.firstName} ${ja.user.lastName}`).join(',') || '',
        startTime: j.startTime,
        startDate: j.startTime,
        duration: j.durationHours,
        package: j.securityOption.title,
        status: j.status,
      })) || [];

    const headers: BasicTableHeader<JobDetailsKey>[] = [
      { key: 'reservationCode', value: 'Reservation Number' },
      { key: 'user', value: 'Customer' },
      { key: 'location', value: 'Location' },
      { key: 'startDate', value: 'Start Date' },
      { key: 'startTime', value: 'Start Time' },
      { key: 'duration', value: 'Duration' },
      { key: 'package', value: 'Package' },
      { key: 'status', value: 'Status' },
    ];

    const getCustomComponentForCell = (field: JobDetailsKey, idx: number) => {
      const c = jobDetails[idx];
      if (field === 'id') {
        return <Button onClick={() => navigate(`/company/jobs/${c.id}`)}>{c.id}</Button>;
      }
      if (field === 'startDate') {
        return format(c.startDate, 'MM/dd/yyyy');
      }

      if (field === 'startTime') {
        return format(c.startTime, 'hh:mm a');
      }
    };

    return (
      <BasicTable
        headers={headers}
        data={jobDetails}
        getCustomComponentForField={getCustomComponentForCell}
      />
    );
  };

  const renderUpcomingJobsSummary = () => {
    if (getJobsSummaryState.isLoading) return <LoadingIndicator isVisible />;
    if (getJobsSummaryState.isError)
      return <p>Error loading jobs: {getErrorMessage(getJobsSummaryState.error)}</p>;

    interface JobDetails {
      id: number;
      reservationCode: string;
      user: string;
      location: string;
      agents: string;
      startDate: Date;
      startTime: Date;
      duration: number;
      package: string;
      status: JobStatus;
    }

    type JobDetailsKey = keyof JobDetails;
    const jobDetails: JobDetails[] =
      jobsSummary?.upcomingJobs?.map((j) => ({
        id: j.id,
        reservationCode: j.reservationCode,
        location: j.location,
        user: `${j.createdBy.firstName} ${j.createdBy.lastName}`,
        agents:
          j.jobAssignments?.map((ja) => `${ja.user.firstName} ${ja.user.lastName}`).join(',') || '',
        startTime: j.startTime,
        startDate: j.startTime,
        duration: j.durationHours,
        package: j.securityOption.title,
        status: j.status,
      })) || [];

    const headers: BasicTableHeader<JobDetailsKey>[] = [
      { key: 'reservationCode', value: 'Reservation Number' },
      { key: 'user', value: 'Customer' },
      { key: 'agents', value: 'Assigned Agent' },
      { key: 'location', value: 'Location' },
      { key: 'startDate', value: 'Start Date' },
      { key: 'startTime', value: 'Start Time' },
      { key: 'duration', value: 'Duration' },
      { key: 'package', value: 'Package' },
      { key: 'status', value: 'Status' },
    ];

    const getCustomComponentForCell = (field: JobDetailsKey, idx: number) => {
      const c = jobDetails[idx];
      if (field === 'id') {
        return <Button onClick={() => navigate(`/company/jobs/${c.id}`)}>{c.id}</Button>;
      }
      if (field === 'startDate') {
        return format(c.startDate, 'MM/dd/yyyy');
      }

      if (field === 'startTime') {
        return format(c.startTime, 'hh:mm a');
      }
    };

    return (
      <BasicTable
        headers={headers}
        data={jobDetails}
        getCustomComponentForField={getCustomComponentForCell}
      />
    );
  };

  if (getCompanySummaryState.isLoading) {
    return <LoadingIndicator isVisible={getCompanySummaryState.isLoading} />;
  }

  return (
    <>
      <title>Dashboard</title>
      <Box maxWidth={'80%'} margin='auto' alignContent='start' textAlign='start' mt='24px'>
        <Box>
          <Box mb='48px'>
            <Typography variant='h2'>Welcome back, {authUser?.firstName}</Typography>
            <Typography variant='body1'>
              Here's a quick glance at a summary of recent and upcoming events for{' '}
              <strong>{companySummary?.company.name}</strong>
            </Typography>
          </Box>
          <Box mb='32px'>
            <Typography variant='h4'>Jobs Needing Assignment</Typography>
            {renderUnassignedJobsSummary()}
          </Box>
          <Box mb='64px'>
            <Typography variant='h4'>Upcoming jobs</Typography>
            {renderUpcomingJobsSummary()}
          </Box>
          <Box display='flex' justifyContent='space-between' gap='2'>
            {renderCompanyStats()}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ManagerHome;
